import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const SkeletonLoader = ({ type = 'default', className = '', count = 1 }) => {
  const { t } = useTranslation();
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(true);

  const loadingMessages = [t('loading_ai_magic'), t('loading_scanning_styles'), t('loading_finding_offers')];

  useEffect(() => {
    if (type === 'other-offers') {
      const interval = setInterval(() => {
        setIsVisible(false);

        setTimeout(() => {
          setCurrentMessageIndex(prev => (prev + 1) % loadingMessages.length);
          setIsVisible(true);
        }, 300); // Half second for fade out/in transition
      }, 5000); // Change message every 5 seconds

      return () => clearInterval(interval);
    }

    return undefined; // Explicit return for consistent-return rule
  }, [type, loadingMessages.length]);
  const renderProductCardSkeleton = () => (
    <div className='skeleton-product-card'>
      <div className='skeleton-image skeleton-pulse' />
      <div className='skeleton-content'>
        <div className='skeleton-text skeleton-title skeleton-pulse' />
        <div className='skeleton-text skeleton-subtitle skeleton-pulse' />
        <div className='skeleton-text skeleton-price skeleton-pulse' />
      </div>
    </div>
  );

  const renderOtherOffersSkeleton = () => {
    return (
      <div className='skeleton-other-offers'>
        {/* Real header with actual text */}
        <div className='skeleton-header'>
          <div>
            <h1 className='mb-0'>{t('title_other_offers')}</h1>
            <div className={`skeleton-loading-text ${isVisible ? 'fade-in' : 'fade-out'}`}>
              {loadingMessages[currentMessageIndex]}
            </div>
          </div>
          <div className='skeleton-navigation d-none d-md-flex'>
            <div className='skeleton-nav-button skeleton-pulse' />
            <div className='skeleton-nav-button skeleton-pulse' />
          </div>
        </div>

        {/* Cards/Tabs skeleton */}
        <div className='skeleton-tabs'>
          {[...Array(3)].map((_, index) => (
            <div key={index} className='skeleton-tab skeleton-pulse'>
              <div className='skeleton-tab-icon' />
              <div className='skeleton-tab-text' />
              <div className='skeleton-tab-subtext' />
            </div>
          ))}
        </div>

        {/* Product grid skeleton */}
        <div className='skeleton-products-grid'>
          {[...Array(count)].map((_, index) => (
            <div key={index} className='skeleton-product-item'>
              <div className='skeleton-product-image skeleton-pulse'>
                <div className='skeleton-source-icon' />
              </div>
              <div className='skeleton-product-info'>
                <div className='skeleton-text skeleton-product-title skeleton-pulse' />
                <div className='skeleton-text skeleton-product-price skeleton-pulse' />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderProductGridSkeleton = () => {
    return (
      <div className='skeleton-product-grid'>
        <div className='skeleton-grid-container'>
          {[...Array(count)].map((_, index) => (
            <div key={index} className='skeleton-grid-item'>
              <div className='skeleton-grid-image skeleton-pulse'>
                <div className='skeleton-source-icon' />
              </div>
              <div className='skeleton-grid-info'>
                <div className='skeleton-text skeleton-grid-title skeleton-pulse' />
                <div className='skeleton-text skeleton-grid-price skeleton-pulse' />
              </div>
            </div>
          ))}
        </div>

        {/* Loading message with fade animation */}
        <div className='skeleton-grid-loading-message'>
          <div className={`skeleton-loading-text ${isVisible ? 'fade-in' : 'fade-out'}`}>
            {loadingMessages[currentMessageIndex]}
          </div>
        </div>
      </div>
    );
  };

  const renderBasicSkeleton = () => (
    <div className='skeleton-basic'>
      {[...Array(count)].map((_, index) => (
        <div key={index} className='skeleton-line skeleton-pulse' />
      ))}
    </div>
  );

  const renderProjectsSkeleton = () => (
    <div className='skeleton-projects'>
      <div className='skeleton-projects-grid'>
        {[...Array(count)].map((_, index) => (
          <div key={index} className='skeleton-project-item'>
            <div className='skeleton-project-image skeleton-pulse' />
            <div className='skeleton-project-info'>
              <div className='skeleton-text skeleton-project-title skeleton-pulse' />
              <div className='skeleton-text skeleton-project-subtitle skeleton-pulse' />
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderCategoriesSkeleton = () => (
    <div className='skeleton-categories'>
      <div className='skeleton-categories-grid'>
        {[...Array(count)].map((_, index) => (
          <div key={index} className='skeleton-category-item'>
            <div className='skeleton-category-image skeleton-pulse' />
            <div className='skeleton-category-info'>
              <div className='skeleton-text skeleton-category-title skeleton-pulse' />
              <div className='skeleton-text skeleton-category-subtitle skeleton-pulse' />
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderListItemsSkeleton = () => (
    <div className='skeleton-list-items'>
      {[...Array(count)].map((_, index) => (
        <div key={index} className='skeleton-list-item'>
          <div className='skeleton-avatar skeleton-pulse' />
          <div className='skeleton-list-content'>
            <div className='skeleton-text skeleton-list-title skeleton-pulse' />
            <div className='skeleton-text skeleton-list-subtitle skeleton-pulse' />
          </div>
        </div>
      ))}
    </div>
  );

  const renderPageSkeleton = () => (
    <div className='skeleton-page'>
      <div className='skeleton-page-content'>
        {[...Array(count)].map((_, index) => (
          <div key={index} className='skeleton-line skeleton-pulse' />
        ))}
      </div>
    </div>
  );

  const getSkeletonContent = () => {
    switch (type) {
      case 'product-card':
        return renderProductCardSkeleton();
      case 'other-offers':
        return renderOtherOffersSkeleton();
      case 'product-grid':
        return renderProductGridSkeleton();
      case 'projects':
        return renderProjectsSkeleton();
      case 'categories':
        return renderCategoriesSkeleton();
      case 'list-items':
        return renderListItemsSkeleton();
      case 'page':
        return renderPageSkeleton();
      case 'basic':
        return renderBasicSkeleton();
      default:
        return renderBasicSkeleton();
    }
  };

  return <div className={`skeleton-loader ${className}`}>{getSkeletonContent()}</div>;
};

export default SkeletonLoader;
